import { ApiError } from "./client";

/**
 * Maps an unknown error to a user-friendly message string and severity.
 * Handles network failures, ApiError (RFC 9457), rate limiting, and server errors.
 */
export function getErrorMessage(error: unknown): string {
  // Network failure — fetch rejected (offline, DNS failure, CORS, etc.)
  if (isNetworkError(error)) {
    return "Unable to reach the server. Please check your internet connection and try again.";
  }

  // Structured API error with status code and ProblemDetails
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return "You're sending requests too quickly. Please wait a moment and try again.";
    }
    if (error.status >= 500) {
      return "The server encountered an error. Please try again later.";
    }
    // Use the RFC 9457 detail or title from ProblemDetails
    return (
      error.problemDetails.detail ||
      error.problemDetails.title ||
      "An unexpected error occurred. Please try again."
    );
  }

  // Generic Error with a message
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred. Please try again.";
}

/**
 * Returns an appropriate AlertTitle for the given error.
 */
export function getErrorTitle(error: unknown): string {
  if (isNetworkError(error)) {
    return "Connection Error";
  }
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return "Rate Limited";
    }
    if (error.status >= 500) {
      return "Server Error";
    }
    return "Validation Error";
  }
  return "Error";
}

/**
 * Returns the MUI Alert severity for the given error.
 */
export function getErrorSeverity(error: unknown): "error" | "warning" | "info" {
  if (error instanceof ApiError && error.status === 429) {
    return "warning";
  }
  return "error";
}

/**
 * Detects network-level failures (offline, DNS, CORS, etc.)
 * These are `TypeError` thrown by `fetch()` when the request never completes.
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const msg = error.message.toLowerCase();
  return (
    msg.includes("fetch") ||
    msg.includes("network") ||
    msg.includes("failed to fetch") ||
    msg.includes("load failed") || // Safari
    msg.includes("networkerror")
  );
}
